// Custom dialog system — replaces native alert/confirm/prompt with styled modals
// Supports dark mode, focus trap, keyboard navigation, ARIA accessibility
import { t } from './i18n.ts';

let activeDialog: HTMLElement | null = null;

function createOverlay() {
  const overlay = document.createElement('div');
  overlay.className = 'custom-dialog-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  return overlay;
}

let dialogSeq = 0;

function createDialog(overlay: HTMLElement, title: string, message: string) {
  const dialog = document.createElement('div');
  dialog.className = 'custom-dialog';

  const idBase = `custom-dialog-${++dialogSeq}`;
  const titleId = `${idBase}-title`;
  const messageId = `${idBase}-message`;

  if (title) {
    const titleEl = document.createElement('h3');
    titleEl.className = 'custom-dialog-title';
    titleEl.id = titleId;
    titleEl.textContent = title;
    dialog.appendChild(titleEl);
  }

  const messageEl = document.createElement('p');
  messageEl.className = 'custom-dialog-message';
  messageEl.id = messageId;
  messageEl.textContent = message;
  dialog.appendChild(messageEl);

  // Callers pass a message and usually no title. The message then names the
  // dialog, otherwise it describes it — either way a screen reader says what
  // is being confirmed, not only which button has focus.
  if (title) {
    overlay.setAttribute('aria-labelledby', titleId);
    overlay.setAttribute('aria-describedby', messageId);
  } else {
    overlay.setAttribute('aria-labelledby', messageId);
  }

  return { dialog, messageId };
}

function createButtonRow() {
  const row = document.createElement('div');
  row.className = 'custom-dialog-buttons';
  return row;
}

function trapFocus(overlay: HTMLElement) {
  function handler(e: Event) {
    if ((e as KeyboardEvent).key !== 'Tab') return;
    const focusable = overlay.querySelectorAll(
      'button, input, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if ((e as KeyboardEvent).shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        // @ts-expect-error — TS strict
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        // @ts-expect-error — TS strict
        first.focus();
      }
    }
  }
  overlay.addEventListener('keydown', handler);
  return handler;
}

function cleanup(overlay: HTMLElement, focusHandler: (e: Event) => void, previousFocus: Element | null) {
  overlay.removeEventListener('keydown', focusHandler);
  overlay.remove();
  activeDialog = null;
  // @ts-expect-error — TS strict
  if (previousFocus && previousFocus.focus) {
    // @ts-expect-error — TS strict
    previousFocus.focus();
  }
}

/**
 * Show a confirm dialog (replaces window.confirm).
 * @param {string} message
 * @param {string} title
 * @returns {Promise<boolean>}
 */
export async function showConfirm(message: string, title = '') {
    if (activeDialog) activeDialog.remove();

  const previousFocus = document.activeElement;
  const overlay = createOverlay();
  const { dialog } = createDialog(overlay, title, message);

  const buttons = createButtonRow();

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'custom-dialog-btn custom-dialog-btn-secondary';
  cancelBtn.textContent = t('dialogs.cancel') || 'Cancel';
  buttons.appendChild(cancelBtn);

  const confirmBtn = document.createElement('button');
  confirmBtn.className = 'custom-dialog-btn custom-dialog-btn-primary';
  confirmBtn.textContent = t('dialogs.ok') || 'OK';
  buttons.appendChild(confirmBtn);

  dialog.appendChild(buttons);
  overlay.appendChild(dialog);

  activeDialog = overlay;

  return new Promise(resolve => {
    const focusHandler = trapFocus(overlay);

    function close(result: boolean) {
      cleanup(overlay, focusHandler, previousFocus);
      resolve(result);
    }

    confirmBtn.addEventListener('click', () => close(true));
    cancelBtn.addEventListener('click', () => close(false));
    overlay.addEventListener('keydown', e => {
      if (e.key === 'Escape') close(false);
    });

    document.body.appendChild(overlay);
    confirmBtn.focus();
  });
}

/**
 * Show a prompt dialog (replaces window.prompt).
 * @param {string} message
 * @param {string} defaultValue
 * @param {string} title
 * @returns {Promise<string|null>}
 */
export async function showPrompt(message: string, defaultValue = '', title = ''): Promise<string | null> {
  if (activeDialog) activeDialog.remove();

  const previousFocus = document.activeElement;
  const overlay = createOverlay();
  const { dialog, messageId } = createDialog(overlay, title, message);

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'custom-dialog-input';
  input.setAttribute('aria-labelledby', messageId);
  input.value = defaultValue;
  dialog.appendChild(input);

  const buttons = createButtonRow();

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'custom-dialog-btn custom-dialog-btn-secondary';
  cancelBtn.textContent = t('dialogs.cancel') || 'Cancel';
  buttons.appendChild(cancelBtn);

  const okBtn = document.createElement('button');
  okBtn.className = 'custom-dialog-btn custom-dialog-btn-primary';
  okBtn.textContent = t('dialogs.ok') || 'OK';
  buttons.appendChild(okBtn);

  dialog.appendChild(buttons);
  overlay.appendChild(dialog);

  activeDialog = overlay;

  return new Promise(resolve => {
    const focusHandler = trapFocus(overlay);

    function close(result: string | null) {
      cleanup(overlay, focusHandler, previousFocus);
      resolve(result);
    }

    okBtn.addEventListener('click', () => close(input.value));
    cancelBtn.addEventListener('click', () => close(null));
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') close(input.value);
    });
    overlay.addEventListener('keydown', e => {
      if (e.key === 'Escape') close(null);
    });

    document.body.appendChild(overlay);
    input.focus();
    input.select();
  });
}

/**
 * Show a star-rating dialog for marketplace templates.
 * @param {string} message
 * @param {string} title
 * @returns {Promise<number|null>} 1-5 or null if cancelled
 */
export async function showStarRating(message: string, title = '') {
  if (activeDialog) activeDialog.remove();

  const previousFocus = document.activeElement;
  const overlay = createOverlay();
  const { dialog } = createDialog(overlay, title, message);

  const starsContainer = document.createElement('div');
  starsContainer.className = 'custom-dialog-stars';
  let selectedScore = 0;

  for (let i = 1; i <= 5; i++) {
    const star = document.createElement('button');
    star.type = 'button';
    star.className = 'custom-dialog-star';
    star.dataset.value = String(i);
    star.setAttribute('aria-label', `${i} star${i > 1 ? 's' : ''}`);
    star.innerHTML = '&#9733;';
    starsContainer.appendChild(star);
  }
  dialog.appendChild(starsContainer);

  const buttons = createButtonRow();

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'custom-dialog-btn custom-dialog-btn-secondary';
  cancelBtn.textContent = t('dialogs.cancel') || 'Cancel';
  buttons.appendChild(cancelBtn);

  const okBtn = document.createElement('button');
  okBtn.className = 'custom-dialog-btn custom-dialog-btn-primary';
  okBtn.textContent = t('dialogs.ok') || 'OK';
  okBtn.disabled = true;
  buttons.appendChild(okBtn);

  dialog.appendChild(buttons);
  overlay.appendChild(dialog);

  activeDialog = overlay;

  function updateStars(value: number) {
    starsContainer.querySelectorAll('.custom-dialog-star').forEach(s => {
      const v = parseInt((s as HTMLElement).dataset.value || '0');
      s.classList.toggle('active', v <= value);
    });
  }

  return new Promise(resolve => {
    const focusHandler = trapFocus(overlay);

    function close(result: number | null) {
      cleanup(overlay, focusHandler, previousFocus);
      resolve(result);
    }

    starsContainer.addEventListener('click', e => {
      const star = (e.target! as HTMLElement).closest('.custom-dialog-star');
      if (!star) return;
      selectedScore = parseInt((star as HTMLElement).dataset.value || '0');
      updateStars(selectedScore);
      okBtn.disabled = false;
    });

    starsContainer.addEventListener('mouseover', e => {
      const star = (e.target! as HTMLElement).closest('.custom-dialog-star');
      if (star) updateStars(parseInt((star as HTMLElement).dataset.value || '0'));
    });

    starsContainer.addEventListener('mouseleave', () => {
      updateStars(selectedScore);
    });

    okBtn.addEventListener('click', () => close(selectedScore || null));
    cancelBtn.addEventListener('click', () => close(null));
    overlay.addEventListener('keydown', e => {
      if (e.key === 'Escape') close(null);
    });

    document.body.appendChild(overlay);
    (starsContainer.querySelector('.custom-dialog-star') as HTMLElement)?.focus();
  });
}
